import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  try {
    const raw = localStorage.getItem('inpace_auth');
    if (raw) {
      const { token } = JSON.parse(raw);
      if (token) config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (_) {}
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem('inpace_auth');
      // soft redirect to login if we're inside the app
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/app')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export default api;

// ---- Auth ----
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }).then(r => r.data),
  me: () => api.get('/auth/me').then(r => r.data),
  changePassword: (current_password, new_password) =>
    api.post('/auth/change-password', { current_password, new_password }).then(r => r.data),
};

// ---- Leads ----
export const leadsApi = {
  list: () => api.get('/leads').then(r => r.data),
  create: (payload) => api.post('/leads', payload).then(r => r.data),
  get: (id) => api.get(`/leads/${id}`).then(r => r.data),
  update: (id, payload) => api.put(`/leads/${id}`, payload).then(r => r.data),
  remove: (id) => api.delete(`/leads/${id}`).then(r => r.data),
  activities: (id) => api.get(`/leads/${id}/activities`).then(r => r.data),
  addActivity: (id, payload) => api.post(`/leads/${id}/activities`, payload).then(r => r.data),
  attachments: (id) => api.get(`/leads/${id}/attachments`).then(r => r.data),
  uploadAttachment: (id, kind, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/leads/${id}/attachments?kind=${kind}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  deleteAttachment: (id, attId) => api.delete(`/leads/${id}/attachments/${attId}`).then(r => r.data),
  attachmentUrl: (id, attId) => {
    const token = JSON.parse(localStorage.getItem('inpace_auth') || '{}').token;
    const base = process.env.REACT_APP_BACKEND_URL;
    return `${base}/api/leads/${id}/attachments/${attId}/download?auth=${token}`;
  },
};

// ---- Proposals ----
export const proposalsApi = {
  list: () => api.get('/proposals').then(r => r.data),
  create: (payload) => api.post('/proposals', payload).then(r => r.data),
  get: (id) => api.get(`/proposals/${id}`).then(r => r.data),
  update: (id, payload) => api.put(`/proposals/${id}`, payload).then(r => r.data),
  remove: (id) => api.delete(`/proposals/${id}`).then(r => r.data),
  email: (id, payload) => api.post(`/proposals/${id}/email`, payload).then(r => r.data),
  downloadPdf: async (id) => {
    const res = await api.get(`/proposals/${id}/pdf`, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Inpace-Solar-Proposal-${id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  openPdf: async (id) => {
    const res = await api.get(`/proposals/${id}/pdf`, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};

// ---- Dashboard ----
export const dashboardApi = {
  stats: () => api.get('/dashboard/stats').then(r => r.data),
  followups: () => api.get('/dashboard/followups').then(r => r.data),
  funnel: () => api.get('/dashboard/funnel').then(r => r.data),
};

// ---- Site Visits ----
export const siteVisitsApi = {
  list: (params) => api.get('/site-visits', { params }).then(r => r.data),
  create: (payload) => api.post('/site-visits', payload).then(r => r.data),
  update: (id, payload) => api.put(`/site-visits/${id}`, payload).then(r => r.data),
  remove: (id) => api.delete(`/site-visits/${id}`).then(r => r.data),
};

// ---- Team (roster — accessible to any auth user) ----
export const teamApi = {
  list: () => api.get('/team').then(r => r.data),
};

// ---- Users (admin) ----
export const usersApi = {
  list: () => api.get('/users').then(r => r.data),
  create: (payload) => api.post('/users', payload).then(r => r.data),
  update: (id, payload) => api.put(`/users/${id}`, payload).then(r => r.data),
  remove: (id) => api.delete(`/users/${id}`).then(r => r.data),
};

// ---- Catalog (BOM Library) ----
export const catalogApi = {
  list: (type) => api.get('/catalog', { params: type ? { type } : {} }).then(r => r.data),
  create: (payload) => api.post('/catalog', payload).then(r => r.data),
  update: (id, payload) => api.put(`/catalog/${id}`, payload).then(r => r.data),
  remove: (id) => api.delete(`/catalog/${id}`).then(r => r.data),
};
